/**
 * useEmailConfirmationStatus Hook
 * Tracks email confirmation status based on actual user authentication state
 * with proper handling for registration flow timing
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { EmailVerificationStatus } from '../../types/email-verification';

interface UseEmailConfirmationStatusReturn {
  status: EmailVerificationStatus;
  isConfirmed: boolean;
  confirmationType: string | null;
  error: string | null;
}

export const useEmailConfirmationStatus = (): UseEmailConfirmationStatusReturn => {
  const [status, setStatus] = useState<EmailVerificationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const { isAuthenticated, user, isLoading } = useAuth();

  useEffect(() => {
    // Don't process while auth is still loading
    if (isLoading) {
      setStatus('pending');
      return;
    }

    // Check URL parameters for confirmation status and errors
    const urlSearchParams = new URLSearchParams(location.search);
    const urlHashParams = new URLSearchParams(location.hash.substring(1));
    
    const confirmationType = urlSearchParams.get('type') || urlHashParams.get('type');
    const errorParam = urlSearchParams.get('error') || urlHashParams.get('error');
    const errorDescription = urlSearchParams.get('error_description') || urlHashParams.get('error_description');

    // Handle errors first
    if (errorParam) {
      setStatus('error');
      setError(errorDescription || errorParam);
      return;
    }

    // Check URL parameters for actual email confirmation
    if (confirmationType === 'signup' || confirmationType === 'recovery') {
      // User clicked email confirmation link - this is actual confirmation
      setStatus('confirmed');
      setError(null);
      return;
    }

    // For authenticated users, check email confirmation status from user profile
    if (isAuthenticated && user) {
      const isEmailConfirmed = user.email_confirmed_at || user.emailVerified;
      
      if (isEmailConfirmed) {
        setStatus('confirmed');
        setError(null);
      } else {
        // User is authenticated but email not confirmed - show pending
        setStatus('pending');
        setError(null);
      }
    } else {
      // Not authenticated - idle state
      setStatus('idle');
      setError(null);
    }
  }, [location, isAuthenticated, user, isLoading]);

  return {
    status,
    isConfirmed: status === 'confirmed',
    confirmationType: new URLSearchParams(location.search).get('type') || 
                     new URLSearchParams(location.hash.substring(1)).get('type'),
    error,
  };
};